import { useEffect, useRef, useState } from 'react';
import {
  RiAddLine,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiAttachmentLine,
  RiAwardLine,
  RiBlurOffLine,
  RiBringToFront,
  RiBrushLine,
  RiCheckboxBlankLine,
  RiContrastDropLine,
  RiCursorLine,
  RiEraserLine,
  RiEyeOffLine,
  RiFullscreenLine,
  RiHand,
  RiImageLine,
  RiLink,
  RiNodeTree,
  RiPinDistanceLine,
  RiPushpinLine,
  RiSendToBack,
  RiShape2Line,
  RiStickyNoteLine,
  RiSubtractLine,
  RiText,
} from 'react-icons/ri';
import { Button } from './ui/button';
import { Hint } from './ui/tooltip';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';
import { BRUSH_COLORS } from '../canvas/core/types';
import type { ToolName } from '../canvas/core/GfxController';
import { BOARD_STYLE_LABEL, type BoardStyle } from '../state/domain';
import { cn } from '@/lib/utils';

type ToolDef = {
  id: ToolName;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  key: string;
};

const TOOL_GROUPS: ToolDef[][] = [
  [
    { id: 'select', label: '选择 / 移动 / 缩放', icon: RiCursorLine, key: 'V' },
    { id: 'hand', label: '平移画布', icon: RiHand, key: 'H / 空格' },
  ],
  [
    { id: 'note', label: '情报卡片', icon: RiStickyNoteLine, key: 'N' },
    { id: 'shape', label: '形状', icon: RiShape2Line, key: 'S' },
    { id: 'text', label: '文本', icon: RiText, key: 'T' },
    { id: 'frame', label: '分区框', icon: RiCheckboxBlankLine, key: 'F' },
  ],
  [
    { id: 'connector', label: '关联线', icon: RiLink, key: 'C' },
    { id: 'rope', label: '联系绳索（红线）', icon: RiPinDistanceLine, key: 'R' },
  ],
  [
    { id: 'pin', label: '图钉', icon: RiPushpinLine, key: 'P' },
    { id: 'circle', label: '圈注', icon: RiBlurOffLine, key: 'O' },
    { id: 'tape', label: '胶带', icon: RiAttachmentLine, key: 'A' },
    { id: 'highlight', label: '荧光标记', icon: RiContrastDropLine, key: 'M' },
    { id: 'brush', label: '手绘标记（圈画 / 下划线）', icon: RiBrushLine, key: 'B' },
    { id: 'stamp', label: '印章', icon: RiAwardLine, key: 'K' },
  ],
  [
    { id: 'redact', label: '遮挡条', icon: RiEyeOffLine, key: 'X' },
    { id: 'eraser', label: '擦除', icon: RiEraserLine, key: 'E' },
  ],
];

const KEYMAP: Record<string, ToolName> = {
  v: 'select',
  h: 'hand',
  n: 'note',
  s: 'shape',
  t: 'text',
  f: 'frame',
  c: 'connector',
  r: 'rope',
  p: 'pin',
  o: 'circle',
  a: 'tape',
  m: 'highlight',
  b: 'brush',
  k: 'stamp',
  x: 'redact',
  e: 'eraser',
};

function SaveIndicator({
  state,
  at,
  error,
}: {
  state: 'clean' | 'dirty' | 'saving' | 'saved' | 'error';
  at: string | null;
  error?: string;
}) {
  const label =
    state === 'saving'
      ? '保存中…'
      : state === 'dirty'
        ? '有未保存更改'
        : state === 'error'
          ? '保存失败'
          : state === 'saved' && at
            ? `已保存 ${new Date(at).toLocaleTimeString('zh-CN', { hour12: false })}`
            : '已保存';

  const tone =
    state === 'saving'
      ? 'text-muted-foreground'
      : state === 'dirty'
        ? 'text-[var(--color-assess)]'
        : state === 'error'
          ? 'text-[var(--color-cord)]'
          : 'text-[var(--color-evidence)]';

  return (
    <div
      className={cn('flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-[3px] text-[10px]', tone)}
      title={state === 'error' ? `保存失败：${error ?? '未知错误'}` : '关联墙改动会自动保存（关页面前也会兜底落盘）'}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          state === 'saving' && 'animate-pulse bg-muted-foreground',
          state === 'dirty' && 'bg-[var(--color-assess)]',
          state === 'error' && 'bg-[var(--color-cord)]',
          (state === 'saved' || state === 'clean') && 'bg-[var(--color-evidence)]'
        )}
      />
      {label}
    </div>
  );
}

export function Toolbar({ onBuildGraph }: { onBuildGraph: () => void }) {
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const boardStyle = useStore(state => state.boardStyle);
  const boardSave = useStore(state => state.boardSave);
  const setBoardStyle = useStore(state => state.setBoardStyle);
  const [stats, setStats] = useState({ total: 0, selected: 0, zoom: 1 });
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!controller) return;
    setStats(controller.stats);
  }, [controller, revision]);

  useEffect(() => {
    if (!controller) return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const tool = KEYMAP[event.key.toLowerCase()];
      if (tool) controller.setTool(tool);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [controller]);

  if (!controller) return null;

  const handlePhoto = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result ?? '');
      controller.addPhoto(controller.viewport.center, src, file.name.replace(/\.[^.]+$/, ''));
      useStore.getState().toast('success', '照片已钉到板上');
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      {/* 左侧竖向工具轨 */}
      <div className="pointer-events-auto absolute left-3 top-3 z-20 flex flex-col items-center gap-1 rounded-[var(--radius-lg)] border border-border bg-card/95 p-1.5 shadow-[0_10px_34px_rgba(27,28,30,0.14)] backdrop-blur">
        {TOOL_GROUPS.map((group, groupIndex) => (
          <div key={groupIndex} className="flex flex-col items-center gap-1">
            {groupIndex > 0 && <Separator className="my-0.5 w-6" />}
            {group.map(tool => (
              <Hint key={tool.id} label={`${tool.label}　${tool.key}`} side="right">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => controller.setTool(tool.id)}
                  className={cn(
                    controller.tool === tool.id &&
                      'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary'
                  )}
                >
                  <tool.icon className="size-4" />
                </Button>
              </Hint>
            ))}
          </div>
        ))}

        <Separator className="my-0.5 w-6" />

        <Hint label="钉一张照片" side="right">
          <Button size="icon" variant="ghost" onClick={() => fileRef.current?.click()}>
            <RiImageLine className="size-4" />
          </Button>
        </Hint>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={event => {
            handlePhoto(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </div>

      {/* 手绘笔设置（选中手绘工具时展开） */}
      {controller.tool === 'brush' && (
        <div className="pointer-events-auto absolute left-[64px] top-3 z-20 flex w-[150px] flex-col gap-1.5 rounded-[var(--radius)] border border-border bg-card/96 p-2 shadow-[0_10px_30px_rgba(27,28,30,0.14)] backdrop-blur">
          <span className="label-caps">手绘笔</span>
          <div className="flex gap-1">
            {(['pencil', 'marker'] as const).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  controller.brushMode = mode;
                  controller.notifyChange();
                }}
                className={cn(
                  'flex-1 rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  controller.brushMode === mode
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {mode === 'pencil' ? '铅笔' : '马克笔'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {BRUSH_COLORS.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => {
                  controller.brushColor = color;
                  controller.notifyChange();
                }}
                className={cn(
                  'size-5 rounded-full border transition-transform',
                  controller.brushColor === color ? 'scale-110 border-foreground' : 'border-border hover:scale-105'
                )}
                style={{ background: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 右上控制区 */}
      <div className="pointer-events-auto absolute right-3 top-3 z-20 flex items-center gap-1 rounded-[var(--radius-lg)] border border-border bg-card/95 px-1.5 py-1 shadow-[0_10px_34px_rgba(27,28,30,0.14)] backdrop-blur">
        <div className="flex items-center gap-0.5">
          <Hint label="缩小">
            <Button size="icon" variant="ghost" onClick={() => controller.zoomOut()}>
              <RiSubtractLine className="size-4" />
            </Button>
          </Hint>
          <Hint label="重置为 100%">
            <Button
              size="sm"
              variant="ghost"
              className="w-12 font-mono text-[11px]"
              onClick={() => controller.resetZoom()}
            >
              {Math.round(stats.zoom * 100)}%
            </Button>
          </Hint>
          <Hint label="放大">
            <Button size="icon" variant="ghost" onClick={() => controller.zoomIn()}>
              <RiAddLine className="size-4" />
            </Button>
          </Hint>
          <Hint label="适应全图　Ctrl+1">
            <Button size="icon" variant="ghost" onClick={() => controller.fitToScreen()}>
              <RiFullscreenLine className="size-4" />
            </Button>
          </Hint>
        </div>

        <Separator orientation="vertical" className="mx-0.5 h-6" />

        <div className="flex items-center gap-0.5">
          <Hint label="撤销　Ctrl+Z">
            <Button size="icon" variant="ghost" onClick={() => controller.undo()}>
              <RiArrowGoBackLine className="size-4" />
            </Button>
          </Hint>
          <Hint label="重做　Ctrl+Shift+Z">
            <Button size="icon" variant="ghost" onClick={() => controller.redo()}>
              <RiArrowGoForwardLine className="size-4" />
            </Button>
          </Hint>
          <Hint label="置于顶层">
            <Button size="icon" variant="ghost" onClick={() => controller.bringToFront()}>
              <RiBringToFront className="size-4" />
            </Button>
          </Hint>
          <Hint label="置于底层">
            <Button size="icon" variant="ghost" onClick={() => controller.sendToBack()}>
              <RiSendToBack className="size-4" />
            </Button>
          </Hint>
        </div>

        <Separator orientation="vertical" className="mx-0.5 h-6" />

        <Select value={boardStyle} onValueChange={value => setBoardStyle(value as BoardStyle)}>
          <SelectTrigger className="h-8 w-[92px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(BOARD_STYLE_LABEL) as [BoardStyle, string][]).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <SaveIndicator state={boardSave.state} at={boardSave.at} error={boardSave.error} />

        <Hint label="按已归档情报自动布局关联墙">
          <Button size="sm" variant="outline" onClick={onBuildGraph} className="gap-1">
            <RiNodeTree className="size-3.5" />
            生成关联墙
          </Button>
        </Hint>
      </div>
    </>
  );
}